import { TZDate } from '@date-fns/tz';

import { createSelector } from 'reselect';

import type { RootState } from 'reducers/index';

import { getProps } from 'utils/selectors';

const getState = (state: RootState) => state.countries;

export const getCountries = createSelector(getState, state => state.data);

export const getCountriesIsFetching = createSelector(getState, ({ isFetching }) => isFetching);

export const getAppCountryCodes = createSelector(
  getCountries,
  countries => Object.values(countries).map(({ code }) => code)
);

export const getCountryByCode = createSelector(
  getCountries,
  getProps<string>,
  (countries, code) => countries[code]
);

export const getCountriesByCodes = createSelector(
  getCountries,
  getProps<string[]>,
  (countries, codes) => codes.map(code => countries[code])
);

export const getTimezonesByCountry = createSelector(
  getCountryByCode,
  (country) => Object.entries(country?.timezonesOffset ?? {})
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => a.label.localeCompare(b.label))
);

// If the isoTimeZone(Europe/Luxembourg) can be parsed,
// it's better to use it because it will handle the Daylight Saving Time
export const resolveIANATimezone = (
  countries: ReturnType<typeof getCountries>,
  isoTimeZone: string | undefined
): string | undefined => {
  const canParse = Boolean(isoTimeZone) && !Number.isNaN(new TZDate(Date.now(), isoTimeZone!).getTime());
  return canParse
    ? isoTimeZone
    : Object.values(countries).find(({ timezones }) => Boolean(timezones?.[isoTimeZone!]))?.timezones?.[isoTimeZone!];
};

export const getTimeZoneByIANATimeZone = createSelector(
  getCountries,
  getProps<string>,
  resolveIANATimezone
);

export const getCountryCurrencyIsoCode = createSelector(getCountryByCode, (country) => country?.currencyIsoCode);

export const getCountryVAT = createSelector(getCountryByCode, (country) => country?.valueAddedTax);

/** CURRENCIES */

const getCurrencyState = createSelector(getState, state => state.currencies);

export const getCurrencyByIsoCode = createSelector(
  getCurrencyState,
  getProps<string>,
  (currencies, isoCode) => currencies[isoCode]
);
